import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'ui-button ui-caps inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-xs font-medium uppercase tracking-wider transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary-strong active:scale-[0.98]',
        destructive:
          'bg-danger/15 text-danger border border-danger/30 hover:bg-danger/25 active:scale-[0.98]',
        outline:
          'border border-border bg-transparent text-soft hover:border-border-strong hover:bg-foreground/5 hover:text-foreground active:scale-[0.98]',
        secondary:
          'bg-foreground/5 text-soft border border-border hover:bg-foreground/10 hover:text-foreground active:scale-[0.98]',
        ghost:
          'text-subtle hover:bg-foreground/5 hover:text-foreground',
        link:
          'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3',
        lg: 'h-10 px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
